import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import api from '../services/api';

/**
 * Handles Zerodha OAuth callback.
 * Zerodha redirects to /zerodha-callback?request_token=XXX&action=login&status=success
 * This page exchanges the request_token for an access_token via our backend, then redirects to settings.
 */
const ZerodhaCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Exchanging Zerodha token...');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const requestToken = params.get('request_token');
    const callbackStatus = params.get('status');

    if (!requestToken || callbackStatus !== 'success') {
      setStatus('error');
      setMessage(params.get('message') || 'Zerodha login was cancelled or failed.');
      setTimeout(() => navigate('/settings'), 3000);
      return;
    }

    api.post<any>('/broker/zerodha/exchange-token', { requestToken })
      .then((r) => {
        if (r.data?.success) {
          setStatus('success');
          setMessage('Zerodha connected successfully! Redirecting to settings...');
          setTimeout(() => navigate('/settings'), 2000);
        } else {
          setStatus('error');
          setMessage(r.data?.error?.message || 'Token exchange failed.');
          setTimeout(() => navigate('/settings'), 3000);
        }
      })
      .catch((e) => {
        setStatus('error');
        setMessage(e.response?.data?.error?.message || e.message || 'Token exchange failed.');
        setTimeout(() => navigate('/settings'), 3000);
      });
  }, [navigate]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 3 }}>
      {status === 'loading' && (
        <>
          <CircularProgress size={48} />
          <Typography variant="h6" color="text.secondary">{message}</Typography>
        </>
      )}
      {status === 'success' && (
        <Alert severity="success" sx={{ maxWidth: 400 }}>
          <Typography variant="body1" fontWeight={700}>Zerodha Connected!</Typography>
          <Typography variant="body2">{message}</Typography>
        </Alert>
      )}
      {status === 'error' && (
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          <Typography variant="body1" fontWeight={700}>Connection Failed</Typography>
          <Typography variant="body2">{message}</Typography>
        </Alert>
      )}
    </Box>
  );
};

export default ZerodhaCallbackPage;
